import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced autosave with in-flight deduping, retry, and tab-hide flushing.
 *
 * @param {Object}   options
 * @param {any}      options.data        current value to persist (compared by JSON)
 * @param {Function} options.onSave      async (data) => void
 * @param {boolean}  [options.enabled]   pause autosave (e.g. while a step is locked)
 * @param {number}   [options.delay]     debounce in ms (default 1500)
 * @param {string}   [options.key]       changing the key resets the "last saved" baseline
 *
 * Failed saves retry on their own with backoff (2s, 4s, 8s … up to 30s), except
 * for 4xx responses, which a retry cannot fix; those wait for the user's Retry.
 *
 * @returns {{ status: 'idle'|'saving'|'saved'|'error'|'offline', lastSavedAt: Date|null,
 *             error: Error|null, isDirty: boolean, saveNow: () => Promise<boolean> }}
 */
export const useAutosave = ({ data, onSave, enabled = true, delay = 1500, key = 'default' }) => {
  const [status, setStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const lastSavedRef = useRef(null);      // JSON of the last successfully saved data
  const latestRef = useRef(data);         // most recent data
  const timerRef = useRef(null);
  const inFlightRef = useRef(null);       // Promise while a save is running
  const pendingRef = useRef(false);       // a change arrived while saving
  const onSaveRef = useRef(onSave);
  const keyRef = useRef(key);
  const offlineRef = useRef(typeof navigator !== 'undefined' ? navigator.onLine === false : false);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);

  onSaveRef.current = onSave;
  latestRef.current = data;

  // Reset baseline when the key changes (new step loaded with fresh defaults).
  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      lastSavedRef.current = safeStringify(data);
      setIsDirty(false);
      setStatus('idle');
      setError(null);
    } else if (lastSavedRef.current === null) {
      lastSavedRef.current = safeStringify(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const runSave = useCallback(async () => {
    if (inFlightRef.current) {
      pendingRef.current = true;
      return inFlightRef.current;
    }

    const snapshot = latestRef.current;
    const serialized = safeStringify(snapshot);
    if (serialized === lastSavedRef.current) {
      setIsDirty(false);
      return true;
    }

    if (offlineRef.current) {
      setStatus('offline');
      return false;
    }

    setStatus('saving');
    setError(null);
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    const saveKey = keyRef.current;

    const promise = (async () => {
      try {
        await onSaveRef.current(snapshot);
        lastSavedRef.current = serialized;
        retryCountRef.current = 0;
        setLastSavedAt(new Date());
        setStatus('saved');
        setIsDirty(safeStringify(latestRef.current) !== serialized);
        return true;
      } catch (err) {
        setError(err);
        setStatus('error');
        const httpStatus = err?.status || err?.response?.status;
        const retryable = !(httpStatus >= 400 && httpStatus < 500);
        if (retryable && !offlineRef.current && keyRef.current === saveKey) {
          const wait = Math.min(2000 * (2 ** retryCountRef.current), 30000);
          retryCountRef.current += 1;
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            if (keyRef.current === saveKey) runSave();
          }, wait);
        }
        return false;
      } finally {
        inFlightRef.current = null;
        if (pendingRef.current) {
          pendingRef.current = false;
          // Re-run for changes that arrived mid-save.
          setTimeout(() => { runSave(); }, 0);
        }
      }
    })();

    inFlightRef.current = promise;
    return promise;
  }, []);

  // Debounce on data change.
  useEffect(() => {
    if (!enabled) return undefined;
    const serialized = safeStringify(data);
    if (lastSavedRef.current === null) {
      lastSavedRef.current = serialized;
      return undefined;
    }
    if (serialized === lastSavedRef.current) {
      setIsDirty(false);
      return undefined;
    }
    setIsDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runSave();
    }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, enabled, delay, runSave]);

  // Flush when the tab is hidden; warn on unload if unsaved.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && enabled && safeStringify(latestRef.current) !== lastSavedRef.current) {
        runSave();
      }
    };
    const handleBeforeUnload = (event) => {
      if (enabled && (inFlightRef.current || safeStringify(latestRef.current) !== lastSavedRef.current)) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    const handleOnline = () => {
      offlineRef.current = false;
      if (safeStringify(latestRef.current) !== lastSavedRef.current) {
        runSave();
      } else {
        setStatus((prev) => (prev === 'offline' ? 'saved' : prev));
      }
    };
    const handleOffline = () => {
      offlineRef.current = true;
      setStatus('offline');
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enabled, runSave]);

  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  const saveNow = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return runSave();
  }, [runSave]);

  /** Mark the current data as the saved baseline without calling onSave (after an explicit save elsewhere). */
  const markSaved = useCallback(() => {
    lastSavedRef.current = safeStringify(latestRef.current);
    setIsDirty(false);
    setLastSavedAt(new Date());
    setStatus('saved');
    setError(null);
  }, []);

  return { status, lastSavedAt, error, isDirty, saveNow, markSaved };
};

function safeStringify(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch (error) {
    return String(Math.random());
  }
}

export default useAutosave;
